import { Formik } from 'formik';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import images from '../../assets/images';
import OTPField from '../../components/common/OTPField';
import SelectBox from '../../components/reactstrap-formik/SelectBox';
import type { ISecureLinkVerificationResponse } from '../../models/federated-account-migration';
import type { IPrivacyPolicy } from '../../models/login';
import type { IConfiguration } from '../../models/profile';
import type { StandardResponse } from '../../models/standard-response';
import { resendAccessOTP, verifyAccessOTP, verifySecureLink } from '../../services/federated-account-migration';
import { getAPIKey } from '../../services/login/login-service';
import { getSupportedLanguages } from '../../services/login/loginApi-service';
import { AppDispatch, store } from '../../store';
import {
  changeLanguage,
  setDefaultLanguage,
  setSupportedLanguages as setSupportedLanguagesFromStore,
} from '../../store/actions/app.Actions';
import type { ISupportedLanguage } from '../../store/state/language.state';
import type { PreloadStatus } from '../../types/stateTypes/stateTypes';
import { companyNameReplace } from '../../utils';
import { Common } from '../../utils/constants';
import { useCountdownTimer } from '../../utils/useCountdownTimer';
import { AccountMigrationOTPSchema } from '../../validation/login/login-validation';
import AccessDenied from './components/AccessDenied';

const OTPVerification = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { token: rawToken } = useParams<{ token?: string }>();
  const token: string = rawToken?.trim() || '';
  const navigate = useNavigate();

  const { secondsLeft, start, formattedTime, isRunning } = useCountdownTimer({
    initialSeconds: 180,
    autoStart: false,
  });

  const browserLang = {
    name: navigator?.language,
    id: 0,
    is_default: false,
    code: navigator?.language,
  };
  let defaultSelectedLang = store.getState().UserData?.language || browserLang;

  const colorStyles = {
    control: (base) => ({
      ...base,
      border: 0,
      boxShadow: 'none',
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? 'var(--themeColor)' : null,
      color: isFocused ? 'white' : '#333333',
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--themeColor)',
      },
    }),
  };

  const initialState = {
    code: '',
  };

  const [language, setLanguage] = useState<ISupportedLanguage | null>(defaultSelectedLang);
  const [supportedLanguages, setSupportedLanguages] = useState<Array<ISupportedLanguage>>([]);
  const [regionDefaultLanguage, setRegionDefaultLanguage] = useState<ISupportedLanguage | null>(null);
  const [apiError, setApiError] = useState<boolean>(false);

  const [privacyPolicyConfig, setPrivacyPolicyConfig] = useState<IPrivacyPolicy[] | null>(null);
  const [privacyPolicy, setPrivacyPolicy] = useState<IPrivacyPolicy | null>(null);

  const [preloadStatus, setPreloadStatus] = useState<PreloadStatus>('loading');
  const [preloadError, setPreloadError] = useState<string>('');
  const [secureLinkData, setSecureLinkData] = useState<ISecureLinkVerificationResponse | null>(null);

  const [isSubmittingOTP, setIsSubmittingOTP] = useState<boolean>(false);

  useEffect(() => {
    preloadSecureLink();
  }, []);

  useEffect(() => {
    if (preloadStatus === 'success') {
      fetchData();
    }
  }, [preloadStatus]);

  useEffect(() => {
    getPrivacyPolicy();
  }, [regionDefaultLanguage]);

  const preloadSecureLink = async () => {
    try {
      const response = await verifySecureLink(token);

      if (response.Data) {
        setSecureLinkData(response.Data);
        setPreloadStatus('success');
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        setPreloadError(error.response.data.message);
      } else {
        setPreloadError(error.toString());
      }
      setPreloadStatus('error');
    }
  };

  const fetchData = async () => {
    try {
      document.getElementById('loading')?.classList.remove('spinner-disable');
      await getSupportedLanguage();
    } finally {
      document.getElementById('loading')?.classList.add('spinner-disable');
    }
  };

  const getSupportedLanguage = async () => {
    try {
      const res = await getSupportedLanguages();
      const rows = res?.data?.rows ?? [];

      if (rows.length === 0) {
        setLanguage(null);
        setSupportedLanguages([]);
        return;
      }

      let defaultLang: ISupportedLanguage | null = null;
      let validBrowserLang: ISupportedLanguage | null = null;
      let secureLinkLang: ISupportedLanguage | null = null;

      for (const lang of rows) {
        if (!defaultLang && lang.is_default) defaultLang = lang;
        if (!validBrowserLang && browserLang?.code?.includes(String(lang.code))) validBrowserLang = lang;
        if (!secureLinkLang && secureLinkData?.language_code === lang.code) secureLinkLang = lang;

        if (defaultLang && validBrowserLang && secureLinkLang) break;
      }

      let language: ISupportedLanguage | null = secureLinkLang;

      if (!language) {
        if (defaultSelectedLang === browserLang) {
          const state = store.getState();
          const currentDefaultLangCode = state?.LanguageInfo?.defaultLanguage?.code;
          const userLangCode = state?.UserData?.language?.code;

          language =
            currentDefaultLangCode !== userLangCode
              ? state?.LanguageInfo?.defaultLanguage || defaultLang
              : validBrowserLang || defaultLang;
        } else {
          language = defaultSelectedLang;
        }
      }

      setLanguage(language);
      setSupportedLanguages(rows);
      setRegionDefaultLanguage(defaultLang);
      dispatch(setSupportedLanguagesFromStore(rows));

      if (language) {
        dispatch(setDefaultLanguage(language));
        if (i18next?.language !== language.code) {
          dispatch(changeLanguage(language));
        }
      }
    } catch (e) {
      setSupportedLanguages([]);
      setLanguage(null);
      setApiError(true);
    }
  };

  const getPrivacyPolicy = async () => {
    try {
      const res = await getAPIKey({ code: Common.ConfigurationKey.PRIVACY_POLICY });
      const configs = res?.data ?? [];

      const privacyPolicyConfig = configs.find(
        (config: IConfiguration) => config.reference_code === Common.ConfigurationKey.PRIVACY_POLICY,
      );

      if (!privacyPolicyConfig?.value) {
        console.error('Privacy Policy Not Found');
        return;
      }

      let policies: IPrivacyPolicy[] = [];
      try {
        policies = JSON.parse(privacyPolicyConfig.value);
      } catch (error) {
        console.error('Error parsing privacy policy JSON:', error);
        return;
      }

      const getPolicyByLanguage = (languageCode: string | undefined) =>
        policies.find((policy) => policy.code === languageCode && policy.title && policy.privacy_policy_url);

      const regionDefaultLanguageName = regionDefaultLanguage?.code ?? '';
      const selectedLanguagePolicy =
        getPolicyByLanguage(language?.code) || getPolicyByLanguage(regionDefaultLanguageName);

      setPrivacyPolicyConfig(policies);
      setPrivacyPolicy(
        selectedLanguagePolicy?.privacy_policy_url && selectedLanguagePolicy.title ? selectedLanguagePolicy : null,
      );
    } catch (e) {
      console.error('Error fetching privacy policy:', e);
    }
  };

  const handleLanguageDropdown = async (values: ISupportedLanguage) => {
    defaultSelectedLang = values;
    let selectedLanguagePolicy: IPrivacyPolicy | null = null;
    if (privacyPolicyConfig) {
      const getPolicyByLanguage = (languageCode) =>
        privacyPolicyConfig.find((policy) => policy.code === languageCode && policy.title && policy.privacy_policy_url);

      const regionDefaultLanguageName =
        regionDefaultLanguage && regionDefaultLanguage.code ? regionDefaultLanguage.code : '';

      selectedLanguagePolicy =
        getPolicyByLanguage(values.code) || (getPolicyByLanguage(regionDefaultLanguageName) as IPrivacyPolicy);
    }
    setLanguage(values);
    setPrivacyPolicy(selectedLanguagePolicy);
    dispatch(changeLanguage(values));
  };

  const handleSubmit = async (values) => {
    setIsSubmittingOTP(true);

    try {
      const response: StandardResponse<boolean> = await verifyAccessOTP(token, values.code);

      if (response?.Data === true) {
        const exp = Date.now() + 10 * 60 * 1000;
        sessionStorage.setItem(
          `${Common.FederatedAccountMigrationSessionKeys.MIGRATION_OTP_VERIFIED}:${token}`,
          JSON.stringify({ ok: true, exp }),
        );
        navigate(`/federated-accounts/migration/manage-access/${token}?lang=${language?.code || 'en'}`);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.toString());
      }
    } finally {
      setIsSubmittingOTP(false);
    }
  };

  const handleResendClick = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();

    try {
      const response: StandardResponse<boolean> = await resendAccessOTP(token);

      if (response?.Data === true) {
        toast.success(response?.Messages);
        start();
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.toString());
      }
    } finally {
    }
  };

  return (
    <>
      <Helmet>
        <title>{companyNameReplace(t('federatedAccountMigration.title'))} </title>
      </Helmet>
      <div className="login-page container-fluid p-0">
        <div className="row h-100 no-gutters">
          <div className="col-md login-background-block">
            <a href="#" title={companyNameReplace(t<string>('login.label.logo'))} className="instavolt-logo">
              <img src={images.logoPNG} alt={companyNameReplace(t<string>('login.label.logo'))} />
            </a>
          </div>
          <div className="col-md-auto login-section">
            <div className="login-outer">
              {preloadStatus === 'error' && <AccessDenied message={preloadError} />}

              {preloadStatus === 'success' && (supportedLanguages?.length > 0 || apiError) && (
                <div className="login-contain">
                  <div className="language-dropdown">
                    <SelectBox
                      styles={colorStyles}
                      name="language"
                      menuPlacement="top"
                      value={language && supportedLanguages.length > 0 ? language : ''}
                      options={supportedLanguages}
                      onChange={(e: ISupportedLanguage) => {
                        handleLanguageDropdown(e);
                      }}
                      getOptionLabel={(opt: ISupportedLanguage) => opt.name as string}
                      getOptionValue={(opt: ISupportedLanguage) => opt.id as number}
                      isClearable={false}
                      placeholder={t('login.placeholder.language')}
                    />
                  </div>
                  <div className="title-outer">
                    <h1 className="title">
                      <span className="icon">
                        <img src={images.emailSVG} alt={t<string>('navbar.title')} />
                      </span>
                    </h1>
                    <p>{t('federatedAccountMigration.message.enterCode')}</p>
                  </div>
                  <Formik
                    key={language?.code || 'en'}
                    initialValues={initialState}
                    onSubmit={handleSubmit}
                    validationSchema={AccountMigrationOTPSchema(t)}
                  >
                    {(props) => {
                      const { handleSubmit } = props;
                      return (
                        <form className="form-secondary" onSubmit={handleSubmit} autoComplete="off" noValidate>
                          <OTPField name="code" />
                          <Form.Group className="form-group">
                            <Button type="submit" className="btn btn-primary w-100" disabled={isSubmittingOTP}>
                              {t('signup.button.confirm')}
                            </Button>
                          </Form.Group>
                          {isRunning && secondsLeft > 0 ? (
                            <div className="clearfix pt-2">
                              <p className="sign-up-otp pb-0">{t('signup.label.otpSentMessage')}</p>
                              <p className="sign-up-otp">
                                <span>
                                  {t('signup.label.resendMessage')} {formattedTime}
                                </span>
                              </p>
                            </div>
                          ) : (
                            <div className="clearfix pt-2">
                              <p className="sign-up-otp pb-0">{t('signup.label.resendTitle')} </p>
                              <p className="sign-up-otp">
                                <a href="#" title={t<string>('signup.button.resend')} onClick={handleResendClick}>
                                  {t('signup.button.resend')}
                                </a>
                              </p>
                            </div>
                          )}
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              )}
            </div>
            {privacyPolicy && privacyPolicy.privacy_policy_url && (
              <p className="copyright">
                {`${companyNameReplace(t('login.label.copyright'))} | `}
                <a
                  href={privacyPolicy.privacy_policy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="privacy-policy-link"
                >
                  {privacyPolicy.title}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OTPVerification;
